import { useEffect, useState } from "react";
import { MAX_LABEL_LENGTH, type Pin } from "@tarkov-callout/shared";
import { t } from "../i18n";
import { pinKindColor, pinKindGlyph } from "../map/pinIcon";
import { used } from "../usage";

/**
 * Everything on the map, as a list you can work through.
 *
 * The map is the good way to see where things are and the bad way to manage
 * them: a pin behind another pin, a pin off the edge of the view, and a
 * freehand line -- which is a few pixels of paint and nothing else to aim at
 * -- are all hard to get hold of by clicking. The card that opens on a click
 * is also the only place a pin can be renamed or deleted, and in the compact
 * layout there is no card at all.
 *
 * So: one row per thing, with the name editable in place and a delete button
 * that does not depend on hitting anything. Picking a row brings the map to it,
 * which is the other half of "I know it is here somewhere".
 */
interface Props {
  /** Every pin in the room; the ones for other maps are filtered out here. */
  pins: Pin[];
  mapId: string;
  activeLayer: string | null;
  selfId: string | null;
  selectedId: string | null;
  onSelect: (pin: Pin) => void;
  onLabel: (id: string, label: string) => void;
  onRemove: (id: string) => void;
}

export function PinList({
  pins,
  mapId,
  activeLayer,
  selfId,
  selectedId,
  onSelect,
  onLabel,
  onRemove,
}: Props) {
  /*
   * Newest first. The thing you want to fix is nearly always the thing you
   * just put down, and a list that reorders itself as pins are renamed would
   * lose your place mid-edit.
   */
  const items = pins
    .filter((pin) => pin.mapId === mapId)
    .slice()
    .sort((a, b) => b.createdAt - a.createdAt);

  if (items.length === 0) return <p className="panel__note">{t("list.empty")}</p>;

  return (
    <ul className="marks">
      {items.map((pin) => (
        <MarkRow
          key={pin.id}
          pin={pin}
          activeLayer={activeLayer}
          selfId={selfId}
          selected={pin.id === selectedId}
          onSelect={onSelect}
          onLabel={onLabel}
          onRemove={onRemove}
        />
      ))}
    </ul>
  );
}

interface RowProps {
  pin: Pin;
  activeLayer: string | null;
  selfId: string | null;
  selected: boolean;
  onSelect: (pin: Pin) => void;
  onLabel: (id: string, label: string) => void;
  onRemove: (id: string) => void;
}

function MarkRow({ pin, activeLayer, selfId, selected, onSelect, onLabel, onRemove }: RowProps) {
  const [draft, setDraft] = useState(pin.label);
  /** A stroke is a shape, not a callout: the server refuses to label one. */
  const isStroke = pin.kind === "draw";
  const onThisFloor = (pin.layer ?? null) === activeLayer;
  /*
   * The same rule the map uses for dragging, and the one the server enforces:
   * anyone may clear anyone's callout, but somebody else's own marker is
   * theirs. Offering a button the server would refuse just looks broken.
   */
  const canRemove = pin.kind !== "self" || pin.authorId === selfId;

  // Follows the room, so a rename by someone else does not fight the field.
  useEffect(() => {
    setDraft(pin.label);
  }, [pin.id, pin.label]);

  const commit = () => {
    if (draft === pin.label) return;
    used("listRename");
    onLabel(pin.id, draft);
  };

  return (
    <li className={`marks__row${selected ? " is-selected" : ""}`}>
      <button
        type="button"
        className="marks__pick"
        style={{ background: pinKindColor(pin.kind, pin.color) }}
        title={t("list.show")}
        aria-label={t("list.show")}
        onClick={() => onSelect(pin)}
      >
        <span aria-hidden="true">{pinKindGlyph(pin.kind)}</span>
      </button>

      <div className="marks__body">
        {isStroke ? (
          <button type="button" className="marks__name marks__name--plain" onClick={() => onSelect(pin)}>
            {t("kind.draw")}
          </button>
        ) : (
          <input
            className="marks__name"
            value={draft}
            maxLength={MAX_LABEL_LENGTH}
            placeholder={t(`kind.${pin.kind}`)}
            onChange={(event) => setDraft(event.target.value)}
            onFocus={() => onSelect(pin)}
            onBlur={commit}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                commit();
                event.currentTarget.blur();
              }
            }}
          />
        )}
        <span className="marks__meta">
          {pin.authorName}
          {!onThisFloor && <> · {pin.layer ?? t("sidebar.groundFloor")}</>}
        </span>
      </div>

      {canRemove && (
        <button
          type="button"
          className="marks__remove"
          title={t("list.remove")}
          aria-label={t("list.remove")}
          onClick={() => onRemove(pin.id)}
        >
          ✕
        </button>
      )}
    </li>
  );
}
